import React from 'react';
import { Edit, Layers, Trash2 } from 'lucide-react';
import {
  getGuestDisplayName,
  getRsvpMeta,
  hasResponseDetails,
  isAttendingStatus,
} from './guestDisplayUtils';

export const GuestCard = ({ guest, modified, onEdit, onFineTune, onDelete, onTagClick }) => {
  const rsvpMeta = getRsvpMeta(guest.rsvp_status);
  const guestName = getGuestDisplayName(guest);
  const adults = Number(guest.rsvp_adult_count || 0);
  const kids = Number(guest.rsvp_kids_count || 0);
  const isAttending = isAttendingStatus(guest.rsvp_status);
  const hasPersonalizedExperience =
    Boolean(guest.media_permissions?.length) || Boolean(guest.subevent_links?.length);

  return (
    <article
      className={`rounded-[28px] border p-4 shadow-sm transition ${
        modified ? "border-yellow-200 bg-yellow-50/40" : "border-gray-100 bg-white"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-base font-black text-gray-950">{guestName}</h3>
          <p className="mt-1 break-words text-[11px] font-bold uppercase tracking-tight text-gray-400">
            {guest.guest?.email_id || "No email"}
          </p>
          {guest.guest?.mobile_number ? (
            <p className="mt-1 text-xs font-semibold text-gray-500">{guest.guest.mobile_number}</p>
          ) : null}
        </div>
        <span className={`shrink-0 rounded-full px-3 py-1.5 text-[10px] font-black uppercase tracking-wide ${rsvpMeta.className}`}>
          {rsvpMeta.label}
        </span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {guest.invite_sent ? (
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-emerald-600">
            Invite sent
          </span>
        ) : (
          <span className="rounded-full bg-gray-100 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-gray-500">
            Not sent yet
          </span>
        )}
        {hasPersonalizedExperience ? (
          <span className="rounded-full bg-purple-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-purple-600">
            Personalized
          </span>
        ) : (
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-wide text-emerald-600">
            Standard invite
          </span>
        )}
      </div>

      <div className="mt-4">
        <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400">Groups</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {guest.group_tags?.length > 0 ? (
            guest.group_tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className="rounded-full bg-purple-100 px-3 py-1 text-[10px] font-bold text-purple-600"
                onClick={() => onTagClick(tag)}
              >
                #{tag.name}
              </button>
            ))
          ) : (
            <span className="rounded-full bg-gray-100 px-4 py-1.5 text-[10px] font-black uppercase text-gray-400">
              General
            </span>
          )}
        </div>
      </div>

      {hasPersonalizedExperience || hasResponseDetails(guest) ? (
        <div className="mt-4 rounded-[22px] bg-gray-50 p-4">
          {hasPersonalizedExperience ? (
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400">Experience</p>
              <p className="mt-1 text-xs font-bold text-gray-600">
                {guest.media_permissions?.length || 0} private file
                {(guest.media_permissions?.length || 0) === 1 ? "" : "s"}
                {guest.subevent_links?.length ? `, ${guest.subevent_links.length} event chapter${guest.subevent_links.length === 1 ? "" : "s"}` : ""}
              </p>
            </div>
          ) : null}

          {hasResponseDetails(guest) ? (
            <div className={hasPersonalizedExperience ? "mt-3 border-t border-white pt-3" : ""}>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gray-400">RSVP details</p>
              <div className="mt-1 space-y-1 text-xs font-semibold leading-5 text-gray-500">
                {isAttending && (guest.rsvp_adult_count || guest.rsvp_kids_count) ? (
                  <p>
                    {adults || 0} adult{adults === 1 ? "" : "s"}
                    {kids ? `, ${kids} kid${kids === 1 ? "" : "s"}` : ""}
                  </p>
                ) : null}
                {guest.rsvp_allergies ? <p>Dietary: {guest.rsvp_allergies}</p> : null}
                {guest.rsvp_message ? <p className="italic text-gray-600">"{guest.rsvp_message}"</p> : null}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onEdit(guest)}
          aria-label={`Edit ${guestName}`}
          title={`Edit ${guestName}`}
          className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-gray-100 text-gray-500 transition hover:bg-[#fff1f8] hover:text-[#ff0099]"
        >
          <Edit size={16} />
        </button>
        <button
          type="button"
          onClick={() => onFineTune(guest)}
          aria-label={`Personalize ${guestName}`}
          title={`Personalize ${guestName}`}
          className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-purple-50 text-purple-500 transition hover:bg-purple-100 hover:text-purple-700"
        >
          <Layers size={16} />
        </button>
        <button
          type="button"
          onClick={() => onDelete(guest.id)}
          aria-label={`Delete ${guestName}`}
          title={`Delete ${guestName}`}
          className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50 text-red-500 transition hover:bg-red-100 hover:text-red-600"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </article>
  );
};
